import type { ModerationAction, ModerationState, SalonModerationResult, SalonRow, SalonsResponse } from '@manaro/contracts'
import type { BadgeTone } from '@/shared/ui/badge.tsx'

export type CatalogFilter = 'all' | ModerationState

type Salon = NonNullable<SalonRow['salon']>

/** A row the review dialog can open: a salon on a backend that knows about moderation. */
export type ModeratableRow = SalonRow & { salon: Salon & { moderation: NonNullable<Salon['moderation']> } }

export const isModeratable = (row: SalonRow): row is ModeratableRow => Boolean(row.salon?.moderation)

export const MODERATION_LABELS: Record<ModerationState, string> = {
  pending: 'Ждёт проверки',
  rejected: 'Ждёт исправлений',
  published: 'В каталоге',
  blocked: 'Заблокирован',
}

export const MODERATION_FILTER_LABELS: Record<ModerationState, string> = {
  pending: 'Ждут проверки',
  rejected: 'Ждут исправлений',
  published: 'В каталоге',
  blocked: 'Заблокированы',
}

export const MODERATION_TONES: Record<ModerationState, BadgeTone> = {
  pending: 'warn',
  rejected: 'info',
  published: 'brand',
  blocked: 'neutral',
}

export const MODERATION_ACTION_LABELS: Record<ModerationAction, string> = {
  approved: 'Опубликовал',
  rejected: 'Отклонил',
  unpublished: 'Снял с публикации',
  resubmitted: 'Владелец отправил на проверку',
}

/** Filter order: what needs the team first. */
export const CATALOG_FILTERS: ModerationState[] = ['pending', 'rejected', 'published', 'blocked']

// Причину читает владелец в письме и в CRM — поэтому заготовки на украинском, как большинство салонов.
export const QUICK_REASONS = [
  'Додайте фото салону.',
  'Додайте послуги з цінами.',
  'Вкажіть адресу салону.',
  'Додайте майстрів і графік роботи.',
  'Схоже на тестовий акаунт — заповніть профіль справжнього салону.',
]

const LANGUAGE_NAMES: Record<string, string> = {
  uk: 'украинском',
  en: 'английском',
  ru: 'русском',
  sk: 'словацком',
}

export const languageName = (language: string | null): string | null =>
  language ? (LANGUAGE_NAMES[language] ?? null) : null

export const moderationState = (row: SalonRow): ModerationState | null => row.salon?.moderation?.state ?? null

const DECISION_SUMMARY: Partial<Record<ModerationAction, string>> = {
  approved: 'опубликован в каталоге',
  rejected: 'отклонён — замечание видно владельцу в CRM',
  unpublished: 'снят с публикации',
}

/** One line for the page after a decision: what happened and whether the owner heard about it. */
export function decisionNotice(title: string, ownerEmail: string, result: SalonModerationResult): string {
  const action = result.moderation.lastEvent?.action
  const what = (action && DECISION_SUMMARY[action]) ?? 'обновлён'
  const mail = result.ownerNotified
    ? 'Владельцу ушло письмо.'
    : `Письмо владельцу не ушло — напишите ему сами: ${ownerEmail}.`
  return `«${title}» ${what}. ${mail}`
}

/** Adds a quick reason on its own line unless it is already there. */
export function appendReason(current: string, reason: string): string {
  if (current.includes(reason)) return current
  const trimmed = current.trimEnd()
  return trimmed ? `${trimmed}\n${reason}` : reason
}

export function applyModeration(data: SalonsResponse, result: SalonModerationResult): SalonsResponse {
  return {
    ...data,
    rows: data.rows.map((row) =>
      row.salon?.id === result.salonId
        ? { ...row, salon: { ...row.salon, status: result.status, moderation: result.moderation } }
        : row,
    ),
  }
}
